import { constant } from '@config/BaseSetting';
import ClientRoutes from '@config/Routes/WebClientRoutes';
import { showFixedAlert } from '@features/application/applicationSlice';
import useForm from '@hooks/useForm';
import useLocalStorage from '@hooks/useLocalStorage';
import { Grid } from '@mui/material';
import { useTogglePlanEntraMutation } from '@services/WebApiService/PlanApi';
import {
  ITBox,
  QboAlert,
  QboFormGroup,
  QboLabelGroup,
  QboTextField,
  QboTypography,
} from '@ui/Components';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

const formSchema = yup.object().shape({
  supportTicket: yup.string().default('').required('Support Ticket is required'),
  escalationTicket: yup.string().default(''),
  description: yup.string().default(''),
});

export default function TogglePlanEntraFormSection({ onClose, plan }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const form = useForm(formSchema);
  const [accessTokenSet] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const [errorMessage, setErrorMessage] = useState();
  const [togglePlanEntra, togglePlanEntraState] = useTogglePlanEntraMutation();

  const resetAll = () => {
    setErrorMessage(undefined);
    form.reset();
    togglePlanEntraState.reset();
  };

  useEffect(() => {
    if (!form.isValid) return;
    togglePlanEntra({
      accessToken: accessTokenSet,
      parameters: {
        'plan-id': plan.planId,
        'support-ticket': form.values.supportTicket,
        'escalation-ticket': form.values.escalationTicket,
        description: form.values.description,
      },
    });
  }, [form.isValid]);

  useEffect(() => {
    if (togglePlanEntraState.data?.['status-code'] === 200) {
      if (togglePlanEntraState.data?.data?.serviceTypeCode) {
        const link = ClientRoutes.toolsRequestStatusPath({
          'service-type': togglePlanEntraState.data?.data?.serviceTypeCode,
        });
        dispatch(
          showFixedAlert({
            descriptionHtml: t('plans_list_page.toggle_plan_entra.success_submitted_with_link', {
              link,
            }),
          })
        );
      } else {
        dispatch(
          showFixedAlert({
            descriptionHtml: t('plans_list_page.toggle_plan_entra.success_submitted'),
          })
        );
      }
      resetAll();
      if (onClose) onClose();
    } else if (togglePlanEntraState.error) {
      form.reset(true);
      setErrorMessage(
        togglePlanEntraState.error?.data?.message ||
          togglePlanEntraState.error?.data?.['status-code'] ||
          togglePlanEntraState.error?.status ||
          (togglePlanEntraState.error.name === 'FetchError'
            ? 'Network error occurred'
            : 'Unknown error occurred')
      );
    }
  }, [togglePlanEntraState.data, togglePlanEntraState.error]);

  const handleSubmit = (e) => {
    e.preventDefault();
    form.validate();
  };

  const handleReset = () => {
    resetAll();
    onClose();
  };

  return (
    <ITBox className="TransferSubscriptionFormPage__wrapper" direction="column">
      <QboFormGroup
        loadingButton={togglePlanEntraState.isLoading}
        onSubmit={handleSubmit}
        withGenerateAndResetButton
        handleOnResetButton={handleReset}
        secondButtonLabel="Cancel"
        firstButtonLabel="Submit">
        {errorMessage && (
          <QboAlert
            variant="outlined"
            type="error"
            style={{
              background: '#fdeded',
              fontSize: '10px !important',
            }}>
            {t('plans_list_page.toggle_plan_entra.error', { error: errorMessage })}
          </QboAlert>
        )}

        <QboAlert
          variant="outlined"
          type="info"
          style={{
            border: '1px solid #ebebeb',
            background: '#f3f3f3',
            fontSize: '10px !important',
            marginTop: '20px',
          }}>
          <QboTypography
            noWrap={false}
            style={{
              fontSize: '13px',
              color: '#767676',
              whiteSpace: 'pre-line',
            }}>
            {t('plans_list_page.toggle_plan_entra.information')}
            <strong>{plan.entraEnabled ? 'Disabled' : 'Enabled'}</strong>
          </QboTypography>
        </QboAlert>

        <ITBox
          className="TransferSubresellerPage__information-box"
          direction="column"
          sx={{ marginBottom: '20px' }}>
          <QboLabelGroup title="Plan ID" description={plan.planId || '-'} />
          <QboLabelGroup title="Plan Name" description={plan.planName || '-'} />
          <QboLabelGroup
            title="Current Entra Status"
            description={plan.entraEnabled ? 'Enabled' : 'Disabled'}
          />
        </ITBox>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <QboTextField
              id="supportTicket"
              placeholder="Support Ticket"
              label="Support Ticket"
              required
              fullWidth
              inputProps={{ maxLength: 225 }}
              value={form.values.supportTicket}
              onChange={(e) => {
                form.change('supportTicket', e.currentTarget.value);
              }}
              errorMessage={form.errors.supportTicket}
            />
          </Grid>

          <Grid item xs={6}>
            <QboTextField
              id="escalationTicket"
              placeholder="Escalation Ticket"
              label="Escalation Ticket"
              fullWidth
              value={form.values.escalationTicket}
              onChange={(e) => {
                form.change('escalationTicket', e.currentTarget.value);
              }}
              inputProps={{ maxLength: 225 }}
            />
          </Grid>
        </Grid>
        <QboTextField
          id="description"
          placeholder="Description"
          label="Description"
          fullWidth
          value={form.values.description}
          onChange={(e) => {
            form.change('description', e.currentTarget.value);
          }}
          inputProps={{ maxLength: 225 }}
        />
      </QboFormGroup>
    </ITBox>
  );
}

TogglePlanEntraFormSection.propTypes = {
  onClose: PropTypes.func,
  plan: PropTypes.object,
};

TogglePlanEntraFormSection.defaultProps = {
  onClose: () => {},
  plan: null,
};
